import type { TokenSchema } from './tokenSchema.ts';

/**
 * Figma Half B — the human-confirm step logic (figma-json-ingestion.md). The server map action
 * runs `@alchmystudio/config`'s `figmaTokensToBrand()` (the single-source mapper — never vendored)
 * and returns a FigmaMapResult; this module is the PURE confirm-state logic the Brand editor drives,
 * so it's unit-tested without the private package.
 *
 * The result-shape types below are a LOCAL structural MIRROR of the mapper's public types
 * (MappedTokens / UnmappedToken / FigmaMapResult in @alchmystudio/config) — the same "mirror the
 * config types, don't import the package in the app proper" pattern tokenSchema.ts uses (importing
 * config would break `npm ci`). The mapper LOGIC is NOT copied — only the data shape at the boundary,
 * which the real result is structurally assignable to. Keep in sync with packages/config/figmaTokens.ts.
 */

/** The brand-token subset the mapper contributes (mirror of config's MappedTokens). */
export interface MappedTokens {
  color?: Partial<Pick<TokenSchema['color'], 'background' | 'copy' | 'copySubtle' | 'primary' | 'primaryHover'>>;
  typography?: {
    fontFamilies?: Partial<TokenSchema['typography']['fontFamilies']>;
    typeScale?: string[];
  };
}

/** A token the mapper could not confidently place (mirror of config's UnmappedToken). */
export interface UnmappedToken {
  name: string;
  kind: 'color' | 'size' | 'font' | 'other';
  value?: string;
  reason: string;
}

/** The mapper's result (mirror of config's FigmaMapResult). */
export interface FigmaMapResult {
  tokens: MappedTokens;
  unmapped: UnmappedToken[];
  notes: string[];
}

export type ColorRole = 'background' | 'copy' | 'copySubtle' | 'primary' | 'primaryHover';
export type FontRole = 'heading' | 'body' | 'accent';

/** The brand-critical colour roles that MUST be resolved before the mapping can be applied/Locked. */
export const BRAND_CRITICAL_ROLES: ColorRole[] = ['background', 'copy', 'primary'];

export const COLOR_ROLES: ColorRole[] = ['background', 'copy', 'copySubtle', 'primary', 'primaryHover'];
export const FONT_ROLES: FontRole[] = ['heading', 'body', 'accent'];

/**
 * The editable confirm state: the designer's current role→value choices, seeded from the mapper's
 * confident output and then overridable (edit a swatch, or assign an unmapped token to a role).
 * Values absent = "not assigned" (so a brand-critical gap is visible + blocks Apply).
 */
export interface ConfirmState {
  color: Partial<Record<ColorRole, string>>;
  fontFamilies: Partial<Record<FontRole, string>>;
  typeScale?: string[];
}

/** Seed the editable state from the mapper's confident result (what it mapped, pre-override). */
export function seedConfirmState(result: FigmaMapResult): ConfirmState {
  const t = result.tokens ?? {};
  return {
    color: { ...(t.color ?? {}) },
    fontFamilies: { ...(t.typography?.fontFamilies ?? {}) },
    typeScale: t.typography?.typeScale,
  };
}

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
/** A trimmed value that reads as a usable colour (we only auto-validate hex; others pass as typed). */
export function isColorValue(v: string | undefined): boolean {
  const s = (v ?? '').trim();
  return s.length > 0 && (HEX.test(s) || !s.startsWith('#')); // hex, or a named/other colour the designer typed
}

/** Which brand-critical roles are still unassigned — Apply/Lock is blocked while this is non-empty. */
export function brandCriticalMissing(state: ConfirmState): ColorRole[] {
  return BRAND_CRITICAL_ROLES.filter((r) => !isColorValue(state.color[r]));
}

/** Project the confirm state back to a MappedTokens partial to merge into the editor's brand.ts. */
export function toMappedTokens(state: ConfirmState): MappedTokens {
  const color: NonNullable<MappedTokens['color']> = {};
  for (const r of COLOR_ROLES) {
    const v = state.color[r]?.trim();
    if (v) color[r] = v;
  }
  const fontFamilies: Partial<TokenSchema['typography']['fontFamilies']> = {};
  for (const r of FONT_ROLES) {
    const v = state.fontFamilies[r]?.trim();
    if (v) fontFamilies[r] = v;
  }
  const out: MappedTokens = {};
  if (Object.keys(color).length) out.color = color;
  const typography: NonNullable<MappedTokens['typography']> = {};
  if (Object.keys(fontFamilies).length) typography.fontFamilies = fontFamilies;
  if (state.typeScale && state.typeScale.length) typography.typeScale = state.typeScale;
  if (Object.keys(typography).length) out.typography = typography;
  return out;
}

/**
 * Merge a confirmed Figma mapping into the editor's current tokens — the SAME shape the AI-extraction
 * path produces, so it flows through the EXISTING Brand-Kit Lock (no second write path). Additive: it
 * only overwrites the roles the designer confirmed; everything else (overlay, textStyles, spacing,
 * grid, button, fonts) is carried forward untouched. Pure, so the merge is unit-tested.
 */
export function applyFigmaTokens(current: TokenSchema, mapped: MappedTokens): TokenSchema {
  return {
    ...current,
    color: { ...current.color, ...(mapped.color ?? {}) },
    typography: {
      ...current.typography,
      fontFamilies: { ...current.typography.fontFamilies, ...(mapped.typography?.fontFamilies ?? {}) },
      ...(mapped.typography?.typeScale ? { typeScale: mapped.typography.typeScale } : {}),
    },
  };
}
